import { CommonModule, Location } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { AuthComponent } from '../../features/auth/pages/auth.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { useResponsiveCols } from '../../shared/composables/useResponsiveCols';
import { useBreakpoint } from '../../shared/composables/useBreakpoint';
import { UserService } from '../../features/user/service/user.service';
import { DialogService } from '../../core/services/dialog.service';
import { PendingKidComponent } from '../../features/user/components/pending-kid/pending-kid.component';
import { PARENT_DIALOG_PANEL_CLASS } from '../../shared/constants/parent-dialog.constants';
import { AiAssistantHeaderComponent } from '../../features/parent-ai-assistant/components/ai-assistant-header/ai-assistant-header.component';
import { ParentAiAssistantComponent } from '../../features/parent-ai-assistant/parent-ai-assistant.component';
import { ParentFamilyContextBarComponent } from './components/parent-family-context-bar/parent-family-context-bar.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ParentIdentityService } from '../../features/identity/services/parent-identity.service';
import { ParentIdentityBannerComponent } from '../../features/identity/components/parent-identity-banner/parent-identity-banner.component';
import { DutyReviewBannerComponent } from '../../features/home/components/duty-review-banner/duty-review-banner.component';
import { DisclosureApprovalBannerComponent } from '../../features/identity/components/disclosure-approval-banner/disclosure-approval-banner.component';
import { HomeDataService } from '../../features/home/services/home-data.service';
import { ParentFamilyContextService } from '../../features/parent-family/parent-family-context.service';
import { FamilyKidsService } from '../../features/parent-family/services/family-kids.service';
import { isPublicAuthRoute } from '../../core/helpers/public-auth-route.helper';
import { GaEventService } from '../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  resolveParentPageSection,
} from '../../core/analytics/parent-analytics.events';

@Component({
  selector: 'app-parent-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MatGridListModule,
    AuthComponent,
    HeaderComponent,
    ParentAiAssistantComponent,
    AiAssistantHeaderComponent,
    ParentFamilyContextBarComponent,
    ParentIdentityBannerComponent,
    DutyReviewBannerComponent,
    DisclosureApprovalBannerComponent,
    TranslatePipe,
  ],
  templateUrl: './parent-layout.component.html',
  styleUrl: './parent-layout.component.scss',
  host: {
    '[class.parent-layout--auth]': 'isAuthRoute()',
  },
})
export class ParentLayoutComponent {
  #router = inject(Router);
  #userService = inject(UserService);
  #dialogService = inject(DialogService);
  #location = inject(Location);
  #identity = inject(ParentIdentityService);
  #homeData = inject(HomeDataService);
  #family = inject(ParentFamilyContextService);
  #familyKids = inject(FamilyKidsService);
  #analytics = inject(GaEventService);
  readonly leftColSpan = useResponsiveCols().leftColSpan;
  readonly rightColSpan = useResponsiveCols().rightColSpan;
  readonly currentBreakpoint = useBreakpoint().currentBreakpoint;

  readonly user = computed(() => this.#userService.user());
  readonly isLoadingUser = computed(() => this.#userService.isLoading());
  readonly showMobileParentDataLoading = computed(
    () =>
      this.currentBreakpoint() === 'XSmall' &&
      !this.isAuthRoute() &&
      this.isLoadingUser(),
  );
  readonly pendingKid = computed(
    () => this.#familyKids.unverifiedChildren()[0] ?? undefined,
  );
  readonly identityLayerEnabled = computed(() => this.#identity.layerEnabled());
  readonly showIdentityBanner = computed(() => {
    if (
      !this.identityLayerEnabled() ||
      !this.#identity.loaded() ||
      this.isAuthRoute() ||
      this.currentRoute() === '/identity/verify'
    ) {
      return false;
    }
    // Newcomer welcome owns setup CTAs — avoid duplicate chrome nags.
    if (this.isWelcomeRoute() && !this.#familyKids.hasChildren()) {
      return false;
    }
    return (
      this.#identity.shouldPromptVerification() ||
      this.#identity.inGracePeriod()
    );
  });
  readonly identityBannerGraceDays = computed(() =>
    this.#identity.graceDaysRemaining(),
  );
  readonly identityBannerInGrace = computed(() => this.#identity.inGracePeriod());
  readonly canShowPendingKidDialog = computed(
    () =>
      !this.identityLayerEnabled() || this.#identity.canExpandFamily(),
  );
  readonly lockKidFamilyScope = computed(
    () =>
      this.identityLayerEnabled() &&
      this.#identity.loaded() &&
      !this.#identity.canExpandFamily(),
  );

  public isAuthRoute = signal(false);
  public currentRoute = signal(this.#router.url);
  readonly isAiAssistant = computed(
    () => this.currentRoute() === '/ai-assistant',
  );
  readonly isWelcomeRoute = computed(() =>
    /\/home\/welcome(?:\/|\?|#|$)/.test(this.currentRoute()),
  );
  readonly isCourseDetail = computed(() =>
    /\/home\/courses\/[^/?#]+/.test(this.currentRoute()),
  );
  readonly isProfilePage = computed(() =>
    /\/profile(?:\/|\?|#|$)/.test(this.currentRoute()),
  );
  readonly isMobile = computed(
    () => this.currentBreakpoint() === 'XSmall',
  );
  readonly showAiAssistant = computed(() => {
    if (this.isProfilePage()) {
      return !this.isMobile();
    }
    return true;
  });
  readonly contentFullWidth = computed(() => this.isCourseDetail());
  readonly showFamilyContextBar = computed(() => {
    if (this.isAuthRoute() || this.isCourseDetail()) {
      return false;
    }
    if (!this.#familyKids.hasChildren()) {
      return false;
    }
    if (this.isProfilePage()) {
      return this.showAiAssistant();
    }
    return true;
  });

  constructor() {
    if (!isPublicAuthRoute(this.#router.url)) {
      void this.#identity.whenConfigReady();
    }

    effect(() => {
      const onAssignments = this.currentRoute().includes('/home/assignments');
      if (onAssignments && this.#family.selectedChildId()?.trim()) {
        this.#homeData.prefetchDuties();
      }
    });

    this.checkRoute();
    this.updateCurrentRoute();
    this.trackParentPageViews();

    effect(() => {
      if (this.pendingKid() && this.canShowPendingKidDialog()) {
        this.#dialogService.open(
          PendingKidComponent,
          { user: this.pendingKid() },
          {
            backdropClass: 'dialog-backdrop',
            panelClass: PARENT_DIALOG_PANEL_CLASS,
          },
        );
      }
      if (
        this.currentRoute() === '/ai-assistant' &&
        this.currentBreakpoint() !== 'XSmall'
      ) {
        this.#location.back();
      }
    });
  }

  private checkRoute() {
    this.isAuthRoute.set(isPublicAuthRoute(this.#router.url));
    this.#router.events.pipe(takeUntilDestroyed()).subscribe(() => {
      this.isAuthRoute.set(isPublicAuthRoute(this.#router.url));
    });
  }

  private updateCurrentRoute() {
    this.#router.events.pipe(takeUntilDestroyed()).subscribe(() => {
      this.currentRoute.set(this.#router.url);
    });
  }

  /** SPA page views scoped to the parent shell (family subdomain). */
  private trackParentPageViews() {
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        const path = event.urlAfterRedirects;
        this.#analytics.trackPageView(path, {
          layout: PARENT_ANALYTICS_LAYOUT,
          page_section: resolveParentPageSection(path),
          is_auth_route: isPublicAuthRoute(path),
        });
      });
  }
}
